import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Switch, Route } from 'react-router-dom';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import { Location } from 'history';
import { DashboardContext } from 'components/screens/PatientDashboard/context';
import { useRoutes, useSimulatorRoutes } from 'hooks';
import StatusBar from './StatusBar';
import LessonChart from './LessonChart';
import Table from './Table';
import { lessonService } from './services';

export interface IProps {
  location: Location;
}

const Lesson = (props: IProps) => {
  const routes = useRoutes();
  const simulatorRoutes = useSimulatorRoutes();

  // ==== PROPS ==== //
  const { location } = props;

  // ==== STATE ==== //
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  // ==== CONTEXT ==== //
  const {
    curriculumProgress,
    setCurriculumProgress,
    patientId,
    selectedAccessRight,
    patientInfo,
  } = useContext(DashboardContext);

  const minDate =
    patientInfo!.accessRights.length > 0
      ? patientInfo!.accessRights[patientInfo!.accessRights.length - 1]
          .startDate
      : new Date().toISOString();
  const maxDate =
    patientInfo!.accessRights.length > 0
      ? patientInfo!.accessRights[0].endDate
      : new Date().toISOString();

  // ===== METHODS ==== //
  const getCurriculumProgress = useCallback(async () => {
    try {
      const fetchedCurriculumProgress =
        await lessonService.getPatientCurriculumProgress(
          patientId!,
          selectedAccessRight!.id,
        );

      setCurriculumProgress({
        ...fetchedCurriculumProgress,
      });

      setIsLoaded(true);
    } catch (e) {
      ErrorInterceptor.error(e);
    }
  }, [patientId, selectedAccessRight, setCurriculumProgress]);

  useEffect(() => {
    if (!curriculumProgress) {
      getCurriculumProgress();
    } else {
      setIsLoaded(true);
    }
  }, [getCurriculumProgress, curriculumProgress]);

  if (isLoaded && curriculumProgress) {
    return (
      <div id="lessons-tab-id" data-testid="lessons-loaded">
        <StatusBar
          totalLessonsCompleted={curriculumProgress.totalLessonsCompleted}
          totalLessonsCompletedThisRx={
            curriculumProgress.totalLessonsCompletedThisRx
          }
          showTotalLessonsAllRx={curriculumProgress.hasMultipleRx}
          lessonsThisWeek={curriculumProgress.totalLessonsCompletedThisWeek}
          location={location}
          listViewRoute={simulatorRoutes.PATIENT_DASHBOARD_LESSONS_TABLE}
          chartViewRoute={simulatorRoutes.PATIENT_DASHBOARD_LESSONS_CHART}
        />
        <Switch>
          <Route
            exact
            path={[
              routes.PATIENT_DASHBOARD_LESSONS,
              routes.PATIENT_DASHBOARD_LESSONS_TABLE,
              simulatorRoutes.PATIENT_DASHBOARD_LESSONS,
              simulatorRoutes.PATIENT_DASHBOARD_LESSONS_TABLE,
            ]}
            render={() => (
              <Table lessonGroups={curriculumProgress.lessonGroups} />
            )}
          />
          <Route
            exact
            path={[
              routes.PATIENT_DASHBOARD_LESSONS_CHART,
              simulatorRoutes.PATIENT_DASHBOARD_LESSONS_CHART,
            ]}
            render={() => (
              <LessonChart
                timezone={selectedAccessRight!.timezone}
                minDate={minDate}
                maxDate={maxDate}
                accessRight={selectedAccessRight!}
                curriculumProgress={curriculumProgress}
              />
            )}
          />
        </Switch>
      </div>
    );
  }

  return <div data-testid="lessons-loading" />;
};

export default Lesson;
